/* NicheSPR: progressive enhancement; checkout and pricing remain Shopify-owned. */
(() => {
  'use strict';
  const productInstances = new WeakMap();
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const strings = () => window.nsTranslations || {};
  const say = (key, fallback) => strings()[key] || fallback;
  const announce = (message) => { const node = q('[data-ns-live]'); if (node) node.textContent = message; };

  function closeDetails(node) {
    if (!node || !node.open) return;
    node.open = false;
    q('summary', node)?.focus();
  }

  function initializeHeader(root) {
    qa('[data-ns-header]', root).forEach(header => {
      if (header.dataset.nsBound) return;
      header.dataset.nsBound = 'true';
      const menu = q('[data-ns-menu]', header);
      const search = q('[data-ns-search]', header);
      q('[data-close-menu]', header)?.addEventListener('click', () => closeDetails(menu));
      q('[data-close-search]', header)?.addEventListener('click', () => closeDetails(search));
      search?.addEventListener('toggle', () => {
        if (search.open) { if (menu) menu.open = false; q('input[type=search]', search)?.focus(); }
      });
      menu?.addEventListener('toggle', () => { if (menu.open && search) search.open = false; });
    });
  }

  function initializeProduct(root) {
    if (productInstances.has(root)) return;
    const form = q('form.ns-product-form', root);
    const variantSelect = q('[data-variant-select]', root);
    const variantData = q('[data-product-variants]', root);
    if (!form || !variantSelect || !variantData) return;
    let variants;
    try { variants = JSON.parse(variantData.textContent); } catch { return; }
    const button = q('[data-add-to-cart]', form);
    const buttonText = q('[data-add-text]', form);
    const error = q('[data-product-error]', root);
    const fieldsets = qa('[data-option-index]', root);
    const personal = q('[data-personalization]', form);
    const nameInput = q('[data-personal-name]', form);
    const photoInput = q('[data-personal-photo]', form);
    const personalError = q('[data-personal-error]', form);
    const preview = q('[data-personal-preview]', root);
    const previewBase = preview ? q('.ns-preview-base', preview) : null;
    const originalPreviewSrc = previewBase?.getAttribute('src');
    const originalPreviewSrcset = previewBase?.getAttribute('srcset');
    let currentVariant = variants.find(variant => String(variant.id) === variantSelect.value);
    let submitting = false;
    let photoURL = null;
    let photoSequence = 0;
    let photoReady = !photoInput;
    let photoDecodeImage = null;
    let nameObserver;
    let purchaseObserver;

    function showError(node, message) {
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
    }

    function showMedia(id) {
      if (!id || !q(`[data-media-panel="${id}"]`, root)) return;
      qa('[data-media-panel]', root).forEach(panel => {
        const active = panel.dataset.mediaPanel === String(id);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (!active) qa('video', panel).forEach(video => video.pause());
      });
      qa('[data-show-media]', root).forEach(thumb => {
        const active = thumb.dataset.showMedia === String(id);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    }

    function fitName() {
      if (!preview || !nameInput) return;
      const box = q('[data-name-window]', preview);
      const text = q('[data-name-preview]', preview);
      if (!box || !text) return;
      text.textContent = nameInput.value.trim();
      const factor = parseFloat(getComputedStyle(preview).getPropertyValue('--name-size')) || 4;
      const baseSize = preview.clientWidth * factor / 100;
      text.style.fontSize = `${baseSize}px`;
      let fitted = baseSize;
      if (text.scrollWidth > box.clientWidth && box.clientWidth > 0) {
        fitted = baseSize * box.clientWidth / text.scrollWidth;
        text.style.fontSize = `${fitted}px`;
      }
      const fontField = q('[data-name-font-size]', form);
      if (fontField && preview.clientWidth > 0) fontField.value = String(Number((fitted / preview.clientWidth * 100).toFixed(4)));
    }

    function updateVariant(updateURL = true) {
      const selected = fieldsets.map(fieldset => q('input:checked', fieldset)?.value);
      currentVariant = fieldsets.length ? variants.find(variant => variant.options.every((option, index) => option === selected[index])) : variants.find(variant => String(variant.id) === variantSelect.value);
      fieldsets.forEach((fieldset, index) => {
        const text = q('[data-option-value]', fieldset);
        if (text) text.textContent = selected[index] || '';
        qa('[data-option-input]', fieldset).forEach(input => {
          const available = variants.some(variant => variant.available && variant.options[index] === input.value && variant.options.every((option, other) => other === index || option === selected[other]));
          input.closest('label')?.classList.toggle('is-unavailable', !available);
        });
      });
      variantSelect.value = currentVariant ? String(currentVariant.id) : '';
      button.disabled = submitting || !currentVariant?.available;
      buttonText.textContent = currentVariant ? (currentVariant.available ? say('add_to_cart', 'Add to cart') : say('sold_out', 'Sold out')) : say('unavailable', 'Unavailable');
      const price = currentVariant?.price || '—';
      q('[data-product-price]', root).textContent = price;
      const compare = q('[data-product-compare]', root);
      compare.textContent = currentVariant?.compare || '';
      compare.hidden = !currentVariant?.compare;
      const stickyPrice = q('[data-sticky-price]', root);
      if (stickyPrice) stickyPrice.textContent = price;
      if (currentVariant) showMedia(currentVariant.media);
      if (previewBase) {
        if (currentVariant?.preview_image) {
          previewBase.removeAttribute('srcset');
          previewBase.src = currentVariant.preview_image;
        } else {
          previewBase.src = originalPreviewSrc;
          if (originalPreviewSrcset) previewBase.setAttribute('srcset', originalPreviewSrcset);
        }
        const mockupField = q('[name="properties[_Preview mockup]"]', form);
        if (mockupField) mockupField.value = currentVariant?.preview_image || originalPreviewSrc;
      }
      if (updateURL) {
        const url = new URL(window.location.href);
        if (currentVariant) url.searchParams.set('variant', currentVariant.id);
        else url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
      showError(error, !currentVariant ? say('combination_unavailable', 'This combination is unavailable. Please choose another option.') : '');
      requestAnimationFrame(fitName);
    }

    function updateCrop() {
      const windowNode = q('[data-photo-window]', root);
      if (!windowNode) return;
      const zoom = q('[data-photo-zoom]', root)?.value || '1';
      const x = q('[data-photo-x]', root)?.value || '50';
      const y = q('[data-photo-y]', root)?.value || '50';
      windowNode.style.setProperty('--crop-zoom', zoom);
      windowNode.style.setProperty('--crop-x', `${x}%`);
      windowNode.style.setProperty('--crop-y', `${y}%`);
      const valueX = q('[data-photo-value-x]', root);
      const valueY = q('[data-photo-value-y]', root);
      const valueZoom = q('[data-photo-value-zoom]', root);
      if (valueX) valueX.value = x;
      if (valueY) valueY.value = y;
      if (valueZoom) valueZoom.value = zoom;
    }

    function resetPhotoPreview() {
      if (photoDecodeImage) { photoDecodeImage.onload = null; photoDecodeImage.onerror = null; photoDecodeImage = null; }
      if (photoURL) { URL.revokeObjectURL(photoURL); photoURL = null; }
      const image = q('[data-photo-preview]', root);
      if (image) image.removeAttribute('src');
      const windowNode = q('[data-photo-window]', root);
      if (windowNode) windowNode.hidden = true;
      const adjust = q('[data-photo-adjust]', root);
      if (adjust) adjust.hidden = true;
    }

    photoInput?.addEventListener('change', () => {
      const sequence = ++photoSequence;
      resetPhotoPreview();
      photoReady = false;
      photoInput.setCustomValidity('');
      showError(personalError, '');
      const file = photoInput.files[0];
      const status = q('[data-photo-status]', root);
      if (!file) { if (status) status.textContent = say('photo_hint', 'Choose a clear photo with your subject in focus.'); return; }
      const maxMB = Number(personal.dataset.maxFileMb) || 5;
      const allowed = (file.type === 'image/jpeg' || file.type === 'image/png' || (!file.type && /\.(jpe?g|png)$/i.test(file.name)));
      let invalid = !allowed ? say('photo_type_error', 'Please choose a JPG or PNG image.') : (file.size > maxMB * 1024 * 1024 ? `${say('photo_size_error', 'Please choose an image smaller than')} ${maxMB} MB.` : '');
      if (invalid) { photoInput.setCustomValidity(invalid); showError(personalError, invalid); if (status) status.textContent = invalid; return; }
      photoURL = URL.createObjectURL(file);
      photoInput.setCustomValidity(say('photo_loading', 'Your photo is still loading. Please wait.'));
      photoDecodeImage = new Image();
      photoDecodeImage.onload = () => {
        if (sequence !== photoSequence) return;
        const image = q('[data-photo-preview]', root);
        const windowNode = q('[data-photo-window]', root);
        const adjust = q('[data-photo-adjust]', root);
        if (image) image.src = photoURL;
        if (windowNode) windowNode.hidden = false;
        if (adjust) adjust.hidden = false;
        photoInput.setCustomValidity('');
        photoReady = true;
        if (status) status.textContent = file.name;
        for (const [selector, value] of [['[data-photo-zoom]', '1'], ['[data-photo-x]', '50'], ['[data-photo-y]', '50']]) {
          const control = q(selector, root); if (control) control.value = value;
        }
        updateCrop();
      };
      photoDecodeImage.onerror = () => {
        if (sequence !== photoSequence) return;
        const message = say('photo_read_error', 'We could not read this photo. Please choose a different JPG or PNG.');
        photoInput.setCustomValidity(message); showError(personalError, message);
        if (status) status.textContent = message;
        resetPhotoPreview();
      };
      photoDecodeImage.src = photoURL;
    });

    nameInput?.addEventListener('input', fitName);
    qa('[data-photo-zoom], [data-photo-x], [data-photo-y]', root).forEach(input => input.addEventListener('input', updateCrop));
    qa('[data-show-media]', root).forEach(thumb => thumb.addEventListener('click', () => showMedia(thumb.dataset.showMedia)));
    qa('[data-option-input]', root).forEach(input => input.addEventListener('change', () => updateVariant(true)));

    const submitHandler = async (event) => {
      // App mode leaves submission and validation to the configured personalizer.
      if (root.dataset.personalizerMode === 'app') return;
      event.preventDefault();
      if (submitting || !currentVariant?.available) return;
      if (!form.reportValidity()) return;
      if (photoInput && !photoReady) { showError(personalError, say('photo_loading', 'Your photo is still loading. Please wait.')); return; }
      if (nameInput && !nameInput.value.trim()) { nameInput.setCustomValidity(say('name_required', 'Please enter your name.')); nameInput.reportValidity(); return; }
      showError(error, '');
      const payload = new FormData(form);
      for (const key of Array.from(payload.keys())) if (key.startsWith('ns-option-')) payload.delete(key);
      payload.set('id', String(currentVariant.id));
      if (nameInput) payload.set('properties[Your name]', nameInput.value.trim());
      submitting = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      buttonText.textContent = say('adding', 'Adding…');
      try {
        const response = await fetch(root.dataset.addUrl, {method:'POST',body:payload,headers:{'Accept':'application/json'}});
        let result;
        try { result = await response.json(); } catch { throw new Error(say('cart_error', 'We could not add this item. Please try again.')); }
        if (!response.ok || result.status) {
          const detail = result.description || result.message;
          throw new Error(typeof detail === 'string' ? detail : say('cart_error', 'We could not add this item. Please try again.'));
        }
        announce(say('added', 'Added to cart.'));
        window.location.assign(root.dataset.cartUrl);
      } catch (problem) {
        showError(error, problem.message || say('network_error', 'Connection interrupted. Please check your connection and try again.'));
        error?.scrollIntoView({block:'nearest',behavior:'smooth'});
        submitting = false;
        button.disabled = !currentVariant?.available;
        button.removeAttribute('aria-busy');
        buttonText.textContent = say('add_to_cart', 'Add to cart');
      }
    };
    form.addEventListener('submit', submitHandler);
    nameInput?.addEventListener('input', () => nameInput.setCustomValidity(''));
    q('[data-jump-to-form]', root)?.addEventListener('click', () => {
      form.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      const first = q('[data-personal-name]', form) || q('[data-option-input]', form) || button;
      first?.focus({preventScroll:true});
    });
    if ('ResizeObserver' in window && preview) { nameObserver = new ResizeObserver(fitName); nameObserver.observe(preview); }
    if ('IntersectionObserver' in window) {
      const sticky = q('.ns-mobile-buy', root);
      if (sticky && button) { purchaseObserver = new IntersectionObserver(entries => { sticky.hidden = entries[0].isIntersecting; }, {threshold:.3}); purchaseObserver.observe(button); }
    }
    updateVariant(false);
    productInstances.set(root, {destroy() { photoSequence++; resetPhotoPreview(); nameObserver?.disconnect(); purchaseObserver?.disconnect(); form.removeEventListener('submit', submitHandler); }});
  }

  function initializeRecommendations(root) {
    qa('[data-ns-recommendations]', root).forEach(node => {
      if (node.dataset.nsBound || node.querySelector('.ns-product-grid')) return;
      node.dataset.nsBound = 'true';
      let observer;
      const load = async () => {
        observer?.disconnect();
        try {
          const response = await fetch(node.dataset.url, {headers:{'Accept':'text/html'}});
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incoming = q('[data-ns-recommendations]', html);
          if (incoming?.querySelector('.ns-product-grid')) node.innerHTML = incoming.innerHTML;
        } catch { /* Optional recommendations never block purchasing. */ }
      };
      if ('IntersectionObserver' in window) { observer = new IntersectionObserver(entries => { if (entries[0].isIntersecting) load(); }, {rootMargin:'300px'}); observer.observe(node); }
      else load();
    });
  }

  function initializeLegacyAccounts() {
    const login = q('#CustomerLoginForm');
    const recover = q('#RecoverPasswordForm');
    const setRecover = (show) => {
      if (!login || !recover) return;
      login.classList.toggle('hide', show); recover.classList.toggle('hide', !show);
      (show ? q('#RecoverEmail') : q('#CustomerEmail'))?.focus();
    };
    q('#RecoverPassword')?.addEventListener('click', event => {event.preventDefault();setRecover(true);});
    q('#HideRecoverPasswordLink')?.addEventListener('click', () => setRecover(false));
    if (location.hash === '#recover') setRecover(true);
    if (q('.reset-password-success')) q('#ResetSuccess')?.classList.remove('hide');
    qa('.address-new-toggle').forEach(button => button.addEventListener('click', () => q('#AddressNewForm')?.classList.toggle('hide')));
    qa('.address-edit-toggle').forEach(button => button.addEventListener('click', () => q(`#EditAddress_${button.dataset.formId}`)?.classList.toggle('hide')));
    qa('.address-delete').forEach(button => button.addEventListener('click', () => {
      if (confirm(button.dataset.confirmMessage || 'Delete this address?') && window.Shopify?.postLink) window.Shopify.postLink(button.dataset.target, {parameters:{_method:'delete'}});
    }));
    if (window.Shopify?.CountryProvinceSelector) {
      if (q('#AddressCountryNew')) new window.Shopify.CountryProvinceSelector('AddressCountryNew','AddressProvinceNew',{hideElement:'AddressProvinceContainerNew'});
      qa('[id^="AddressCountry_"]').forEach(select => { const id = select.id.slice('AddressCountry_'.length); new window.Shopify.CountryProvinceSelector(select.id,`AddressProvince_${id}`,{hideElement:`AddressProvinceContainer_${id}`}); });
    }
  }

  function initialize(root = document) {
    initializeHeader(root);
    qa('[data-ns-product]', root).forEach(initializeProduct);
    initializeRecommendations(root);
  }

  document.addEventListener('click', event => {
    const minus = event.target.closest('[data-quantity-minus]');
    const plus = event.target.closest('[data-quantity-plus]');
    if (minus || plus) {
      const input = q('[data-quantity]', (minus || plus).closest('.ns-quantity'));
      if (!input) return;
      const min = Number(input.min) || 0;
      if (!Number.isFinite(input.valueAsNumber)) input.value = String(min);
      if (plus) input.stepUp(); else input.stepDown();
      if (input.valueAsNumber < min) input.value = String(min);
      input.dispatchEvent(new Event('change',{bubbles:true}));
    }
    qa('[data-ns-menu][open], [data-ns-search][open], .ns-desktop-nav details[open]').forEach(details => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    qa('[data-ns-menu][open], [data-ns-search][open], .ns-desktop-nav details[open]').forEach(closeDetails);
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => qa('[data-ns-product]', event.target).forEach(root => productInstances.get(root)?.destroy()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => {initialize();initializeLegacyAccounts();});
  else {initialize();initializeLegacyAccounts();}
})();
